import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import { useEffect, useMemo, useState } from "react";
import { HomeShow, HomeViewAs } from "@core/constants";
import SelectShow from "@pages/Home/components/HomeHeader/SelectShow";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import { SelectAmongWithContext } from "@vms/SelectAmong";
import useQueryState from "@hooks/useQueryState";
import SelectDisplayMetric from "@vms/SelectDisplayMetric";
import { useDisplayMetricContext, useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { DEFAULT_SELECT_AMONG_DATA, SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import {
    HomeBrandsRawNormDataItem,
    HomeBrandsRawNormRequestPayload,
    useHomeBrandsRawNormData
} from "@api/useHomeBrandsRawNormData";
import { useGetAmongPayload } from "@vms/SelectAmong/useGetAmongPayload";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useMetric } from "@core/CommonDataContext";
import { CopyTableOptions, useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import {
    BrandsViewRawNormTableConfigProps,
    useBrandsViewRawNormTableConfig
} from "@pages/Home/components/BrandsView/BrandsViewRawNormTableConfig";
import { getAmongTypeKey, useGetSelectedAmongTypes } from "@models/Among";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";
import { ProfileExportPptRequest } from "@pages/Profile/components/Header/Header";
import { Metric } from "@models/Metric";

const BrandsView: FCX = () => {
    const { search } = useHomeContext();
    const { data: homeSearchData, resetSearch } = useHomeSearchSettingsContext();
    const [show, setShow] = useQueryState<HomeShow>('show', HomeShow.Raw);
    const { displayMetricId, setDisplayMetricId } = useDisplayMetricContext();
    const [amongData, setAmongData] = useState<SelectAmongData>(DEFAULT_SELECT_AMONG_DATA);

    const getAmongPayload = useGetAmongPayload(true);

    const amongPayload = useMemo(
        () => getAmongPayload(amongData),
        [getAmongPayload, amongData]
    );

    const payload = useMemo(
        (): HomeBrandsRawNormRequestPayload => ({
            ...amongPayload,
            metric: displayMetricId as HomeBrandsRawNormRequestPayload["metric"],
        }),
        [amongPayload, displayMetricId]
    );

    const { data: rawData, isLoading } = useHomeBrandsRawNormData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(i => {
            const columns: { [key: string]: number | null } = {};
            i.columns.forEach((i, key) => {
                if (show === HomeShow.Norm && displayMetricId !== null)  {
                    columns[`column${key}`] = i.norm ?? null;
                }
                else {
                    columns[`column${key}`] = i.sortValue;
                }
            })

            return {
                ...i,
                ...columns,
            };
        }),
        [dataForSorting, show, displayMetricId]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
    );

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): BrandsViewRawNormTableConfigProps => ({
            data,
            show,
            amongPayload,
        }),
        [data, show, amongPayload]
    );

    const tableConfig = useBrandsViewRawNormTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const getSelectedAmongTypes = useGetSelectedAmongTypes();
    const selectedAmongTypes = getSelectedAmongTypes(amongPayload);

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage', },
                ...selectedAmongTypes.map(t => ({
                    id: `column${getAmongTypeKey(t.id, t.type)}`,
                    name: `column${getAmongTypeKey(t.id, t.type)}`,
                }))
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomeBrandsRawNormDataItem);
                setSortingDirection(direction);
            },
        }),
        [setSortingDirection, setSortingKey, sortingDirection, sortingKey, selectedAmongTypes]
    );

    const displayMetric = useMetric(displayMetricId);

    const copyTableOptions = useMemo(
        (): CopyTableOptions => ({
            hideAmong: true,
            additionalRows: [[`Metric: ${displayMetric ? displayMetric?.name : 'Base'}`]]
        }),
        [displayMetric]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length, copyTableOptions);

    useEffect(() => {
        if (show !== HomeShow.Raw && show !== HomeShow.Norm) {
            setShow(HomeShow.Raw);
        }
    }, [show, setShow]);

    const exportPayload = useMemo(
        (): ProfileExportPptRequest => {
            if (displayMetricId === null) {
                return {};
            }

            return { trendMetric: displayMetricId as ProfileExportPptRequest['trendMetric'] };
        },
        [displayMetricId]
    );

    const disableShow = useMemo(() => displayMetricId === Metric.PolarityScore, [displayMetricId]);

    useEffect(() => {
        setDisplayMetricId(Metric.KnowIt);
    }, [setDisplayMetricId]);

    useEffect(() => {
        if (homeSearchData?.displayMetric) {
            setDisplayMetricId(homeSearchData.displayMetric);
        }
    }, [homeSearchData, setDisplayMetricId])

    const initialAmongData = useMemo(
        () => {
            if (homeSearchData?.among && homeSearchData?.viewAs === HomeViewAs.Brands) {
                const result: Partial<SelectAmongData> = {};
                let optionsLimit = 10;

                const addTypeByLimit = (type: keyof SelectAmongData) => {
                    const items = homeSearchData.among[type];

                    if (optionsLimit > 0) {
                        result[type] = items.slice(0, optionsLimit);
                        optionsLimit = Math.max(0, optionsLimit - (items?.length ?? 0));
                    }
                };

                const optionsGroupsKeys: (keyof SelectAmongData)[] = [
                    'respondentTypes',
                    'restaurants',
                    'cStores',
                    'supermarkets',
                    'hotels',
                    'brands',
                    'segmentsAndOnSites',
                    'deliveryServices',
                ];

                optionsGroupsKeys.forEach(addTypeByLimit);

                return result;
            }
        },
        [homeSearchData]
    );

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}>
                <SelectShow
                    show={show}
                    setShow={setShow}
                    disabled={disableShow}
                    availableOptions={[HomeShow.Raw, HomeShow.Norm]}
                />
            </HomeHeader>
            <HomeSubHeader>
                <SelectDisplayMetric
                    value={displayMetricId}
                    setValue={setDisplayMetricId}
                    onChange={resetSearch}
                />
                <SelectAmongWithContext
                    label="FOR:"
                    defaultThumbnailText="Please select"
                    showTotalOption
                    useOptionsLimit
                    noRespondentTypes
                    allowMultiBrands
                    onDataChange={setAmongData}
                    initialData={initialAmongData}
                    onApply={resetSearch}
                />
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={48}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton
                                data={copyTableContent}
                            />
                        </AsideButtons>
                    </WindowScrollerTable>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
            <HomeActionsBar exportPayload={exportPayload}/>
        </>
    )
}

export default BrandsView;